import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Suggestion } from "@edufadder/shared-types";
import { Chip, colors, spacing } from "@edufadder/ui";
import { useAuth } from "../lib/auth-context.js";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import { AccountsClosedMessage } from "../components/AccountsClosed.js";
import { useRegistrationOpen } from "../lib/site.js";
import * as s from "../styles.js";

const STATUS: Record<string, { tone: "blue" | "green" | "neutral"; label: string }> = {
  new: { tone: "blue", label: "Waiting for a curator" },
  accepted: { tone: "green", label: "Accepted — being researched" },
  declined: { tone: "neutral", label: "Not taken forward" },
  duplicate: { tone: "neutral", label: "Already covered" },
};

export function SuggestPage(): JSX.Element {
  const registrationOpen = useRegistrationOpen();
  const { me, loading } = useAuth();
  const [idea, setIdea] = useState("");
  const [where, setWhere] = useState("");
  const [source, setSource] = useState("");
  const [mine, setMine] = useState<Suggestion[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (me) api.mySuggestions().then(setMine).catch(() => setMine([]));
  }, [me]);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      const created = await api.createSuggestion({ idea, where_seen: where, source_hint: source });
      setMine((m) => [created, ...m]);
      setIdea("");
      setWhere("");
      setSource("");
      setMessage("Thank you! A curator will look into it. You can follow its status below and on your dashboard.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send that just now.");
    }
  }

  return (
    <div style={{ ...s.wide, maxWidth: 820 }}>
      <h1 style={{ ...s.heading1, fontSize: 34 }}>Suggest an edufad</h1>
      <hr style={s.goldRule} />
      <p style={s.lead}>
        Met an idea about teaching or learning that EduFadder should know about? Or think a note needs a correction? Tell us.
        Curators research every suggestion; accepted ideas go through the full evidence review and, once published, reach every
        EduFadder user automatically.
      </p>

      {loading ? null : !me ? (
        <div style={{ ...s.card, background: colors.goldTint }}>
          Please <Link to="/login">sign in</Link> to send a suggestion — it lets curators follow up and lets you see what happened
          to it.{" "}
          {registrationOpen ? (
            <>
              No account yet? <Link to="/register">Create a free account</Link>.
            </>
          ) : (
            <AccountsClosedMessage />
          )}
        </div>
      ) : (
        <form onSubmit={(e) => void submit(e)} style={{ ...s.card, marginTop: spacing(6) }}>
          <label style={s.label}>
            The idea, in a sentence
            <textarea required minLength={8} maxLength={600} style={s.textarea} value={idea} onChange={(e) => setIdea(e.target.value)} placeholder="e.g. “Fidget spinners help children with attention problems focus in class.”" />
          </label>
          <label style={s.label}>
            Where you met it (optional)
            <input maxLength={600} style={s.input} value={where} onChange={(e) => setWhere(e.target.value)} placeholder="e.g. a teacher-training slide deck, a parenting blog" />
          </label>
          <label style={s.label}>
            A source, if you know one (optional)
            <input maxLength={600} style={s.input} value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. a DOI, article title, or link" />
          </label>
          {error && <p style={s.errorText}>{error}</p>}
          {message && <p style={{ color: colors.supportGreen, fontWeight: 600 }}>{message}</p>}
          <button type="submit" style={s.buttonPrimary}>
            Send suggestion
          </button>
        </form>
      )}

      {me && mine.length > 0 && (
        <section>
          <h2 style={s.sectionTitle}>Your suggestions</h2>
          {mine.map((m) => (
            <div key={m.id} style={s.card}>
              <p style={{ margin: `0 0 ${spacing(2)}`, fontWeight: 600 }}>{m.idea}</p>
              <Chip tone={STATUS[m.status]?.tone ?? "neutral"}>{STATUS[m.status]?.label ?? m.status}</Chip>
              {m.review_note && <p style={{ margin: `${spacing(2)} 0 0` }}>Curator's note: {m.review_note}</p>}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
